'use client';

import { useEffect, useRef, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { X, Rocket, AppWindow, Megaphone, ImageIcon, Loader2, Check, MessagesSquare } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PopoverPortal } from '@/components/ui/popover-portal';
import { coverGradient } from '@/lib/marketplace/cover';
import { emitWorkspaceListChanged } from '@/lib/workspaces/list-events';

type OpenDetail = {
  contentId: string;
  name?: string;
  description?: string;
  /** Suggested category slug (server-derived default). */
  category?: string;
  /** True when the app is already published — switches the flow to "Publish an
   *  update" (adds a "What's new" field + update-flavoured announce copy). */
  alreadyPublished?: boolean;
};

const EVENT_NAME = 'workspace:open-publish-app';

// The //apps marketplace categories (kept in sync with the seed tree).
const APP_CATEGORIES: Array<{ slug: string; label: string }> = [
  { slug: 'games', label: 'Games' },
  { slug: 'collaboration', label: 'Collaboration' },
  { slug: 'productivity', label: 'Productivity' },
  { slug: 'creative', label: 'Creative' },
  { slug: 'dev', label: 'Dev' },
  { slug: 'data', label: 'Data' },
  { slug: 'ai', label: 'AI' },
  { slug: 'utilities', label: 'Utilities' },
  { slug: 'live', label: 'Live' },
  { slug: 'templates', label: 'Templates' },
];

/** Imperative API: open the Publish app dialog for an owned widget. */
export function openPublishAppDialog(detail: OpenDetail): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<OpenDetail>(EVENT_NAME, { detail }));
}

type Phase = 'form' | 'announce';

/**
 * Mounted once per workspace shell. The dedicated publish flow for an app:
 *
 *  - Phase 1 (form): name + description + category + "List in marketplace"
 *    toggle, plus a "What's new" field when publishing an update. Submits to
 *    `/api/content/<id>/publish`, which freezes a version, mints the listing
 *    channel under `//apps/<category>`, and (when listed) repoints its release.
 *  - Phase 2 (announce, optional): once listed, a PREFILLED post composer so the
 *    creator can tell the world (a launch post, or an update changelog). On by
 *    default; one click to Share, one to Skip. Posts into the app's own listing
 *    channel so it shows on the app page + propagates to followers' feeds.
 */
export function PublishAppDialog() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [contentId, setContentId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('utilities');
  const [listed, setListed] = useState(true);
  const [alreadyPublished, setAlreadyPublished] = useState(false);
  const [whatsNew, setWhatsNew] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Phase 2 (announce) state.
  const [phase, setPhase] = useState<Phase>('form');
  const [channelSlug, setChannelSlug] = useState<string | null>(null);
  // The frozen version's content id — attached to the announce post so it
  // carries the Open CTA (the announce post IS the promo post now; publish
  // no longer auto-creates a hidden release post).
  const [publishedWidgetContentId, setPublishedWidgetContentId] = useState<string | null>(null);
  const [wasUpdate, setWasUpdate] = useState(false);
  const [postTitle, setPostTitle] = useState('');
  const [postBody, setPostBody] = useState('');
  const [postMediaUrl, setPostMediaUrl] = useState<string | null>(null);
  const [postMediaType, setPostMediaType] = useState<'image' | 'video'>('image');
  const [imgBusy, setImgBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onOpen = (e: Event) => {
      const d = (e as CustomEvent<OpenDetail>).detail;
      if (!d?.contentId) return;
      setContentId(d.contentId);
      setName(d.name ?? t('workspace.publish.myAppPlaceholder'));
      setDescription(d.description ?? '');
      setCategory(d.category && APP_CATEGORIES.some((c) => c.slug === d.category) ? d.category : 'utilities');
      setListed(true);
      setAlreadyPublished(!!d.alreadyPublished);
      setWhatsNew('');
      setError(null);
      setBusy(false);
      setPhase('form');
      setChannelSlug(null);
      setPostMediaUrl(null);
      setPostMediaType('image');
      setOpen(true);
    };
    window.addEventListener(EVENT_NAME, onOpen);
    return () => window.removeEventListener(EVENT_NAME, onOpen);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy]);

  if (!open) return null;

  // Preview of the listing slug the server will mint (//apps/<category>/<appname>).
  // Mirrors publish.ts handle derivation; the server appends -2, -3, … on collision.
  const previewHandle =
    (name.trim() || 'my app').toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') || 'app';
  const previewSlug = `apps/${category}/${previewHandle}`;

  const close = () => !busy && setOpen(false);
  const finishAndClose = () => {
    emitWorkspaceListChanged();
    setOpen(false);
  };

  const publish = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/content/${contentId}/publish`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          listed,
          category,
          title: name.trim() || t('workspace.publish.myAppPlaceholder'),
          // Omit when empty — the publish schema accepts a string, not null.
          ...(description.trim() ? { content: description.trim() } : {}),
          ...(alreadyPublished && whatsNew.trim() ? { message: whatsNew.trim() } : {}),
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(j?.error?.message || j?.error || `Publish failed (${res.status})`);
      }
      const j = (await res.json().catch(() => null)) as
        | { channelSlug?: string | null; isUpdate?: boolean; contentId?: string | null }
        | null;
      // `contentId` in the response is the FROZEN version's content id (not
      // the working widget) — the id the announce post attaches.
      if (j?.contentId) setPublishedWidgetContentId(j.contentId);

      emitWorkspaceListChanged();
      // Let an owning canvas node refresh its publish pill (Publish → Published).
      window.dispatchEvent(new CustomEvent('workspace:widget-published', { detail: { contentId } }));

      // Listed → offer the prefilled announce composer. Unlisted (no channel) →
      // nothing to announce, just close.
      if (listed && j?.channelSlug) {
        const isUpd = alreadyPublished || !!j.isUpdate;
        setChannelSlug(j.channelSlug);
        setWasUpdate(isUpd);
        if (isUpd) {
          setPostTitle(`${name.trim() || t('workspace.publish.myAppPlaceholder')}: what's new`);
          setPostBody(
            whatsNew.trim()
              ? `Just shipped an update to ${name.trim() || 'my app'}. What's new: ${whatsNew.trim()}`
              : `Just shipped an update to ${name.trim() || 'my app'} on Robutler.`,
          );
        } else {
          setPostTitle(`${name.trim() || t('workspace.publish.myAppPlaceholder')} is live on Robutler`);
          setPostBody(
            description.trim()
              ? `Just launched ${name.trim() || 'my app'}. ${description.trim()}`
              : `Just launched ${name.trim() || 'my app'} on Robutler. Check it out!`,
          );
        }
        setPhase('announce');
        setBusy(false);
        return;
      }
      setOpen(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      if (phase === 'form') setBusy(false);
    }
  };

  const pickMedia = async (file: File | undefined) => {
    if (!file) return;
    const isVideo = file.type.startsWith('video/');
    setImgBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('type', 'post');
      const r = await fetch('/api/upload', { method: 'POST', body: fd });
      if (!r.ok) {
        const j = await r.json().catch(() => null);
        throw new Error(j?.error || `${isVideo ? t('workspace.publish.video') : t('workspace.publish.image')} upload failed`);
      }
      const j = (await r.json()) as { url?: string };
      if (j?.url) {
        setPostMediaUrl(j.url);
        setPostMediaType(isVideo ? 'video' : 'image');
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setImgBusy(false);
    }
  };

  const share = async () => {
    if (!channelSlug) return finishAndClose();
    setBusy(true);
    setError(null);
    try {
      // The announce post is THE promo post: it lands in the listing channel
      // and carries the widget attachment, so it renders the Open CTA.
      const res = await fetch('/api/posts', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          title: postTitle.trim() || undefined,
          content: postBody.trim(),
          channel_slug: channelSlug,
          ...(postMediaUrl ? { media_url: postMediaUrl, media_type: postMediaType } : {}),
          ...(publishedWidgetContentId ? { attached_content_id: publishedWidgetContentId } : {}),
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(j?.error?.message || j?.error || `Could not post (${res.status})`);
      }
      finishAndClose();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  const cover = coverGradient(contentId, null);
  const headerLabel =
    phase === 'announce' ? 'Share the news' : alreadyPublished ? t('workspace.publish.publishAnUpdate') : t('workspace.publish.publishApp');
  const HeaderIcon = phase === 'announce' ? Megaphone : Rocket;

  return (
    <PopoverPortal
      role="dialog"
      ariaLabel={headerLabel}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 220,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '6vh 4vw',
      }}
      className="bg-black/40 backdrop-blur-sm"
    >
      <div
        className="glass-panel relative flex max-h-[88vh] w-[min(560px,96vw)] flex-col overflow-hidden rounded-2xl shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center gap-2 px-4 py-3 shadow-[inset_0_-1px_0_rgba(15,23,42,0.06)] dark:shadow-[inset_0_-1px_0_rgba(0,0,0,0.35)]">
          <HeaderIcon className="h-4 w-4 text-primary" />
          <h2 className="text-sm font-semibold tracking-tight">{headerLabel}</h2>
          <button
            type="button"
            aria-label={t('workspace.canvas.node.close')}
            onClick={close}
            className="ml-auto rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          {phase === 'form' ? (
            <>
              {/* Live preview card */}
              <div className="mb-4 overflow-hidden rounded-xl border border-border/60">
                <div className="flex h-16 items-center justify-center text-white" style={{ background: cover.css }}>
                  <AppWindow className="h-6 w-6" strokeWidth={1.6} />
                </div>
                <div className="px-3 py-2.5">
                  <div className="truncate text-[13px] font-medium">{name || t('workspace.publish.myAppPlaceholder')}</div>
                  <div className="truncate text-[11px] text-muted-foreground">//apps/{category}</div>
                </div>
              </div>

              <label className="mb-1 block text-[11px] font-medium text-muted-foreground">Name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mb-3 w-full rounded-lg border border-border/60 bg-background px-3 py-2 text-sm outline-none focus:border-primary/60"
                placeholder={t('workspace.publish.myAppPlaceholder')}
              />

              <label className="mb-1 block text-[11px] font-medium text-muted-foreground">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="mb-3 w-full resize-none rounded-lg border border-border/60 bg-background px-3 py-2 text-sm outline-none focus:border-primary/60"
                placeholder={t('workspace.publish.whatDoesItDo')}
              />

              {alreadyPublished && (
                <>
                  <label className="mb-1 block text-[11px] font-medium text-muted-foreground">
                    What&apos;s new in this version? <span className="text-muted-foreground/60">(optional)</span>
                  </label>
                  <textarea
                    value={whatsNew}
                    onChange={(e) => setWhatsNew(e.target.value)}
                    rows={2}
                    className="mb-3 w-full resize-none rounded-lg border border-border/60 bg-background px-3 py-2 text-sm outline-none focus:border-primary/60"
                    placeholder={t('workspace.publish.changesPlaceholder')}
                  />
                </>
              )}

              <label className="mb-1 block text-[11px] font-medium text-muted-foreground">Category</label>
              <div className="mb-3 flex flex-wrap gap-1.5">
                {APP_CATEGORIES.map((c) => (
                  <button
                    key={c.slug}
                    type="button"
                    onClick={() => setCategory(c.slug)}
                    className={cn(
                      'rounded-full px-3 py-1 text-xs transition-colors',
                      category === c.slug
                        ? 'bg-primary text-primary-foreground'
                        : 'border border-border/60 text-muted-foreground hover:bg-accent',
                    )}
                  >
                    {c.label}
                  </button>
                ))}
              </div>

              {/* Publishing IS listing — no toggle. Show the home channel that
                  will be minted (//apps/<category>/<appname>), boosted in the
                  marketplace. A number is appended if the name is taken. */}
              <label className="mb-1 block text-[11px] font-medium text-muted-foreground">{t('workspace.publish.homeChannel')}</label>
              <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-foreground/5 px-3 py-2.5">
                <MessagesSquare className="h-4 w-4 shrink-0 text-muted-foreground" />
                <code className="min-w-0 flex-1 truncate text-[13px] font-medium">//{previewSlug}</code>
              </div>
              <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                {t('workspace.publish.channelNotePre')} <span className="font-mono">//apps</span>{t('workspace.publish.channelNotePost')}
              </p>
            </>
          ) : (
            <>
              {/* Announce phase: prefilled, optional. */}
              <div className="mb-3 flex items-center gap-2 rounded-lg bg-emerald-500/10 px-3 py-2 text-[12px] text-emerald-700 dark:text-emerald-300">
                <Check className="h-4 w-4 shrink-0" />
                {wasUpdate ? 'Update published.' : `${name || 'Your app'} is live.`} Share the news with the community,
                or skip.
              </div>

              <label className="mb-1 block text-[11px] font-medium text-muted-foreground">{t('workspace.publish.postTitle')}</label>
              <input
                value={postTitle}
                onChange={(e) => setPostTitle(e.target.value)}
                className="mb-3 w-full rounded-lg border border-border/60 bg-background px-3 py-2 text-sm outline-none focus:border-primary/60"
                placeholder={t('workspace.publish.title')}
              />

              <label className="mb-1 block text-[11px] font-medium text-muted-foreground">Message</label>
              <textarea
                value={postBody}
                onChange={(e) => setPostBody(e.target.value)}
                rows={4}
                className="mb-3 w-full resize-none rounded-lg border border-border/60 bg-background px-3 py-2 text-sm outline-none focus:border-primary/60"
                placeholder={t('workspace.publish.whatsNewPlaceholder')}
              />

              {postMediaUrl ? (
                <div className="relative mb-3 overflow-hidden rounded-lg border border-border/60">
                  {postMediaType === 'video' ? (
                    <video src={postMediaUrl} controls muted playsInline className="max-h-48 w-full object-cover" />
                  ) : (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={postMediaUrl} alt="" className="max-h-48 w-full object-cover" />
                  )}
                  <button
                    type="button"
                    onClick={() => setPostMediaUrl(null)}
                    className="absolute right-1.5 top-1.5 rounded-md bg-black/50 p-1 text-white hover:bg-black/70"
                    aria-label={t('workspace.publish.removeMedia')}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={imgBusy}
                  className="mb-3 inline-flex items-center gap-1.5 rounded-lg border border-border/60 px-3 py-1.5 text-[12px] text-muted-foreground hover:bg-accent disabled:opacity-50"
                >
                  {imgBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}
                  {imgBusy ? t('workspace.files.uploading') : t('workspace.publish.addMedia')}
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*,video/*"
                className="hidden"
                onChange={(e) => void pickMedia(e.target.files?.[0])}
              />
            </>
          )}

          {error && <p className="mt-3 text-xs text-red-500">{error}</p>}
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 px-4 py-3 shadow-[inset_0_1px_0_rgba(15,23,42,0.06)] dark:shadow-[inset_0_1px_0_rgba(0,0,0,0.35)]">
          {phase === 'form' ? (
            <>
              <button
                type="button"
                onClick={close}
                disabled={busy}
                className="rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-accent disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void publish()}
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-lg bg-foreground px-4 py-2 text-sm font-medium text-background hover:opacity-90 disabled:opacity-50"
              >
                <Rocket className="h-4 w-4" />
                {busy ? 'Publishing…' : alreadyPublished ? t('workspace.node.publishUpdate') : t('workspace.publish.publish')}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={finishAndClose}
                disabled={busy}
                className="rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-accent disabled:opacity-50"
              >
                Skip
              </button>
              <button
                type="button"
                onClick={() => void share()}
                disabled={busy || !postBody.trim()}
                className="inline-flex items-center gap-1.5 rounded-lg bg-foreground px-4 py-2 text-sm font-medium text-background hover:opacity-90 disabled:opacity-50"
              >
                <Megaphone className="h-4 w-4" />
                {busy ? t('workspace.publish.posting') : t('workspace.publish.sharePost')}
              </button>
            </>
          )}
        </div>
      </div>
    </PopoverPortal>
  );
}
